import { DOMAIN, urlParam } from "../config";
import { t } from "../i18n";
import { firePixelAndWait } from "../pixels";
import { handlesModalType, goToSuccessPage } from "../success-page";
import { selectedCountry, indiaRuleError } from "./phone-input";
import { emailNeedsReview } from "./email-check";

const FALLBACK_ERROR = "We could not send your order. Please try again.";
const NO_PAYMENT_LINK_MESSAGE =
	"Your order has been received. We could not open the online payment page - we will contact you shortly.";
//26 answered with a position in this list, 29 with a name. Both are kept:
//a page still running the legacy bundle asks the old library.
const PHONE_ERRORS = {
	0: "Valid phone number",
	1: "Invalid phone number (Bad country code)",
	2: "Invalid phone number (Too short)",
	3: "Invalid phone number (Too long)",
	4: "Invalid phone number (Local only)",
	5: "Invalid phonenumber (Invalid length)",
	IS_POSSIBLE: "Valid phone number",
	INVALID_COUNTRY_CODE: "Invalid phone number (Bad country code)",
	TOO_SHORT: "Invalid phone number (Too short)",
	TOO_LONG: "Invalid phone number (Too long)",
	IS_POSSIBLE_LOCAL_ONLY: "Invalid phone number (Local only)",
	INVALID_LENGTH: "Invalid phonenumber (Invalid length)",
};

//One latch for the whole page, like the legacy bundle: the duplicated
//checkout forms submit the same order, so a submit on either blocks both.
let wasSubmitted = false;

function showErrors(errors, form) {
	Object.entries(errors).forEach(([key, value]) => {
		if (key === "countdown_expires") {
			form.querySelector(".sdk-register-btn").textContent = value;
		} else {
			const p = document.createElement("p");
			p.innerHTML = value;
			form.querySelector(".sdk-form-errors").appendChild(p);
		}
	});
	wasSubmitted = false;
}

function startCountdown(button, duration, message, form) {
	button.setAttribute("type", "button");
	button.innerHTML = message + " " + duration;

	const interval = setInterval(() => {
		if (--duration) {
			button.innerHTML = message + " " + duration;
		} else {
			clearInterval(interval);
			//Put the button back to a submit, otherwise the visitor is left
			//with a button that can no longer submit the form at all.
			button.setAttribute("type", "submit");
			showErrors({ countdown_expires: t("countdown") }, form);
		}
	}, 1000);

	return interval;
}

function resetButton(button, countdown) {
	if (countdown !== undefined) {
		clearInterval(countdown);
	}
	button.setAttribute("type", "submit");
	button.innerHTML = t("register");
}

function serializeForm(form) {
	const params = new URLSearchParams();
	form.querySelectorAll("input:not(.hidden-input), select").forEach((field) => {
		if (!field.name || field.disabled) {
			return;
		}
		if (
			field.type === "submit" ||
			field.type === "button" ||
			field.type === "file"
		) {
			return;
		}
		if ((field.type === "checkbox" || field.type === "radio") && !field.checked) {
			return;
		}
		params.append(field.name, field.value);
	});
	return params;
}

function codPaymentRedirect(data) {
	// Payment link received -> send the customer to the payment page
	if (data && typeof data === "object" && data.url) {
		window.location.replace(data.url);
		return;
	}

	// No payment link. The order is already registered in the CRM at this
	// point, so stay on the page and show the CRM message under the form.
	const message =
		(typeof data === "string" && data) ||
		(data && data.message) ||
		NO_PAYMENT_LINK_MESSAGE;

	showDone(message, null);
}

//Both outcomes end the same way when there is nowhere to send the customer:
//the order stands, so tell them on the page they are already on.
function showDone(message, form) {
	const scope = form || document;

	scope.querySelectorAll(".sdk-form-done").forEach((done) => {
		const p = document.createElement("p");
		p.innerHTML = message;
		done.appendChild(p);
	});
}

function bindForm(form) {
	form.addEventListener("submit", (e) => {
		e.preventDefault();
		//This listener binds before the legacy bundle's; stopping propagation
		//here means a cached pre-guard core.min.js can never double-submit.
		e.stopImmediatePropagation();

		if (wasSubmitted) {
			return;
		}
		if (emailNeedsReview(form)) {
			return;
		}
		wasSubmitted = true;

		form.querySelectorAll(".sdk-errors, .sdk-form-errors").forEach((el) => {
			el.innerHTML = "";
		});

		const button = form.querySelector(".sdk-register-btn");
		const params = serializeForm(form);
		params.append("form-type", form.dataset.formType || "");
		params.append("transaction_id", urlParam("transaction_id"));

		let countdown;
		const telField = form.querySelector("input[type=tel]");
		//The widget arrives in a chunk of its own: a customer pressing the
		//button before it loaded, or on a page where it never did, still
		//sends the order, with the number as typed.
		const iti = telField && window.intlTelInput && window.intlTelInput.getInstance(telField);

		if (iti) {
			//From 29 these methods throw rather than answer while the utils
			//script is still loading, and the customer pressing the button in
			//that first moment must be told to try again, not dropped into an
			//error nobody handles.
			let valid = false;
			let reason = 0;
			try {
				valid = iti.isValidNumber();
				if (!valid) {
					reason = iti.getValidationError();
				}
			} catch (error) {
				valid = false;
			}

			if (!valid) {
				showErrors({ 0: PHONE_ERRORS[reason] || PHONE_ERRORS[0] }, form);
				return;
			}

			const fullNumber = iti.getNumber();
			const dialCode = selectedCountry(iti).dialCode;

			const indiaError = indiaRuleError(dialCode, fullNumber.replace("+" + dialCode, ""));
			if (indiaError) {
				showErrors({ 0: indiaError }, form);
				return;
			}

			params.append("full", fullNumber);
			params.append("areaCode", "+" + dialCode);
			params.append("phone", fullNumber.replace("+" + dialCode, ""));

			countdown = startCountdown(button, 30, t("wait"), form);
		}

		if (urlParam("is_test") == 1) {
			params.append("is_test", "1");
		}

		fetch(DOMAIN + "/funnels-sdk/save-data", {
			method: "POST",
			body: params,
		})
			.then((response) => response.json())
			.then((response) => {
				if (response.success) {
					clearInterval(countdown);

					if (response.success.data) {
						//Public extension point: integrations (e.g. a partner's
						//event bridge on its checkouts) react to a registered
						//order here, before the payment redirect.
						document.dispatchEvent(
							new CustomEvent("funnels-sdk:order-submitted", {
								detail: { form, data: response.success.data },
							}),
						);
						firePixelAndWait("lead", 1500).then(() => {
							button.innerHTML = "Success";
							//A funnel that takes the money on delivery has nothing to
							//pay for now: it ends on the page the CRM names for this
							//landing. Without one the order still stands, so say so
							//rather than leaving the customer on a silent form.
							if (window.MODAL_TYPE === "codRedirect") {
								if (!goToSuccessPage(response.success.redirect_url)) {
									showDone(t("order_received"), form);
								}
								return;
							}
							codPaymentRedirect(response.success.data);
						});
					} else {
						resetButton(button, countdown);
						wasSubmitted = false;
					}
				} else if (response.error) {
					resetButton(button, countdown);
					showErrors(response.error, form);
				}
			})
			.catch(() => {
				resetButton(button, countdown);
				showErrors({ 0: FALLBACK_ERROR }, form);
			});
	});
}

export function submitFlow() {
	const forms = document.querySelectorAll(".sdk-form");

	if (!forms.length) {
		return;
	}
	//An outcome this library does not implement stays with the legacy bundle.
	if (!handlesModalType()) {
		return;
	}

	forms.forEach(bindForm);
}
